
import styled, { createGlobalStyle } from "styled-components"
import { motion } from "framer-motion"
import LogoComponent from "../subComponents/LogoComponent"
import SocialIcons from "../subComponents/SocialIcons"
import PowerButton from "../subComponents/PowerButton"
import React, { useEffect, useState } from "react"

/* ================= GLOBAL FONT ================= */

const GlobalFont = createGlobalStyle`
@import url('https://fonts.googleapis.com/css2?family=Ubuntu+Mono:wght@400;700&display=swap');
`

/* ================= MAIN ================= */

const MainContainer = styled.div`
background: #000;
width: 100vw;
min-height: 100vh;
position: relative;
overflow-x: hidden;
color: white;
`

const Container = styled.div`
padding: 2rem;
`

/* ================= BIG BACKGROUND TITLE ================= */

const BigTitle = styled(motion.h1)`
  position: fixed;
  top: 3%;
  left: 30%;
  transform: translateX(-50%);
  font-size: 8rem;
  font-weight: 900;
  letter-spacing: 10px;
  color: rgba(255, 255, 255, 0.18);
  white-space: nowrap;
  pointer-events: none;
  z-index: 0;

  /* 1024px and below */
  @media (max-width: 1024px) {
    font-size: 6rem;
    letter-spacing: 6px;
  }

  /* 768px and below */
  @media (max-width: 768px) {
    font-size: 4.5rem;
    letter-spacing: 4px;
  }

  /* 425px and below */
  @media (max-width: 426px) {
    font-size: 3.5rem;
    top:100px;
    left:30px;
    
  }

  /* 320px and below */
  @media (max-width: 320px) {
    font-size: 3rem;
    top:100px;
    left:30px;
    letter-spacing: 1px;
  }
`;

/* ================= GLOW ================= */

const BlurGlow = styled.div`
position: absolute;
top: 35%;
left: 50%;
transform: translateX(-50%);
width: 60%;
height: 300px;
background: radial-gradient(
  circle,
  rgba(255,255,255,0.08),
  transparent 70%
);
filter: blur(80px);
pointer-events: none;
`

/* ================= TIMELINE ================= */

const Timeline = styled.div`
position: fixed;
top: 0;
left: 50%;
transform: translateX(-50%);
width: 1px;
height: 100%;

background: rgba(255,255,255,0.4);
box-shadow: 0 0 4px rgba(255,255,255,0.4);

z-index: 0;

  @media (max-width: 768px) {
    display: none;
  }
`

/* ================= LAYOUT ================= */

const Wrapper = styled(motion.div)`
position: relative;
display: flex;
flex-direction: column;
padding-bottom: 6rem;
`

const Row = styled(motion.div)`
position: relative;
display: flex;
justify-content: center;
align-items: flex-start;

gap: 5rem;

max-width: 1200px;
margin: 0 auto;

/* 🔥 Safe horizontal breathing */
padding: 80px 3rem;

@media (min-width: 1025px) and (max-width: 1439px) {
  max-width: 900px;     /* 🔥 shrink overall width */
  gap: 2.5rem;           /* 🔥 reduce gap */
  padding: 70px 2rem;    /* 🔥 reduce side padding */
}

@media (max-width: 1024px) {
  gap: 3.5rem;
  padding: 70px 3rem;
}

@media (max-width: 768px) {
  flex-direction: column;
  gap: 4rem;
  align-items: center;
}

@media (max-width: 426px) {
  justify-content: flex-end;
  padding: 60px 1rem;
}

  @media (max-width: 376px) {
  justify-content: flex-end;
  padding-left: 2.5rem;
}

@media (max-width: 320px) {
  justify-content: center;
  padding-left: 0px;   /* 🔥 remove that shift */
  padding-right: 0px;
}

@media (max-width: 321px) {
  display: block;      /* 🔥 disable flex completely */
  padding: 60px 0;
}
`

/* ================= CARD ================= */

const Card = styled(motion.div)`
flex: 0 0 45%;
max-width: 500px;

background: rgba(31,31,31,0.65);
backdrop-filter: blur(24px);
border-radius: 22px;
padding: 2.5rem;
border: 1px solid rgba(255,255,255,0.15);
display: flex;
flex-direction: column;
z-index: 2;


@media (min-width: 1025px) and (max-width: 1439px) {
  flex: 0 0 380px;   /* 🔥 fixed stable width */
  max-width: 420px;
}

/* 🔥 Tablet Fix */
@media (max-width: 1024px) {
  flex: 0 0 350px;   /* 🔥 reduced */
  max-width: 350px;
  padding: 1.6rem;   /* slightly tighter */
}

/* Mobile */
@media (max-width: 900px) {
  flex: 1;
  max-width: 100%;
}

@media (max-width: 768px) {
  flex: 1;
  max-width: 600px;
  width: 100%;
}

@media (max-width: 426px) {
  flex: 0 0 260px;
  max-width: 260px;
  width: 260px;
  padding: 1rem;
}

@media (max-width: 321px) {
  width: 65%;          /* 🔥 percentage is safer here */
  max-width: 260px;
  margin: 20px auto;      /* center manually */
}
`

const CenterLeft = styled(Card)`
  align-self: center;
`

/* ================= LEFT ================= */

const Company = styled.h2`
font-size: 1.9rem;
font-weight: 700;
margin-bottom: 0.6rem;   /* tighter */

@media (max-width: 1024px) {
  font-size: 1.5rem;
}
`

const Role = styled.p`
font-size: 1.2rem;
font-weight: 500;
margin-bottom: 1rem;   /* 🔥 bigger gap after role */

@media (max-width: 1024px) {
  font-size: 1rem;
}
`

const Meta = styled.p`
opacity: 0.65;
font-size: 0.95rem;
margin-bottom: 0.4rem;

@media (max-width: 1024px) {
  font-size: 0.85rem;
}
`

const Badge = styled.span`
display: inline-block;
align-self: flex-start;
margin-top: 1rem;
padding: 0.35rem 1rem;
border-radius: 999px;
background: rgba(255,255,255,0.08);
border: 1px solid rgba(255,255,255,0.25);
font-size: 0.75rem;
letter-spacing: 0.6px;
`

const SkillWrapper = styled.div`
margin-top: 1.8rem;
display: flex;
flex-wrap: wrap;
gap: 0.7rem;
`

const Skill = styled.span`
display: inline-flex;
align-items: center;
justify-content: center;

padding: 0.45rem 1rem;
border-radius: 999px;

background: rgba(255, 255, 255, 0.08);   /* 🔥 visible background */
border: 1px solid rgba(255, 255, 255, 0.18);

font-size: 0.85rem;
font-weight: 500;
color: #eaeaea;

transition: all 0.2s ease;

&:hover {
  background: rgba(255, 255, 255, 0.15);
  transform: translateY(-2px);
}
`

/* ================= RIGHT ================= */

const RightText = styled.div`
font-family: 'Ubuntu Mono', monospace;
font-size: 1.05rem;
line-height: 1.9;
color: #f0f0f0;
letter-spacing: 0.3px;
`

const BulletList = styled.ul`
list-style: none;
margin-top: 2rem;
padding: 0;
display: flex;
flex-direction: column;
gap: 1.4rem;
`

const BulletItem = styled.li`
position: relative;
padding-left: 1.8rem;
font-family: 'Ubuntu Mono', monospace;
font-size: 1.02rem;
line-height: 1.8;
color: #e6e6e6;

&::before {
  content: "▸";
  position: absolute;
  left: 0;
  color: #ffffff;
  font-size: 1.1rem;
}
`

/* ================= ANIMATION VARIANTS ================= */


const cardVariants = {
  hidden: { opacity: 0, y: 60 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8 }
  }
}

/* ================= COMPONENT ================= */

const Experience = () => {
      const [showContent, setShowContent] = useState(false)

        useEffect(() => {
            const timer = setTimeout(() => {
            setShowContent(true)
            }, 1000)   // 1 second delay

            return () => clearTimeout(timer)
        }, [])
  return (
    <MainContainer>

      <GlobalFont />

      <Container>
        <PowerButton />
        <LogoComponent theme="dark" />
        <SocialIcons theme="dark" />
      </Container>

      <BigTitle
  initial={{ opacity: 0, y: -40 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ duration: 1 }}
>
  EXPERIENCE
</BigTitle>

      <BlurGlow />
      <Timeline />

<Wrapper
  initial={{ opacity: 0 }}
  animate={showContent ? { opacity: 1 } : { opacity: 0 }}
  transition={{ duration: 0.8 }}
>


<Row>

  <CenterLeft
    variants={cardVariants}
    initial={{ y: 60, opacity: 0 }}
    animate={showContent ? { y: 0, opacity: 1 } : {}}
    transition={{ duration: 0.8 }}
  >

    <Company>QuizBot AI</Company>

    <Role>AI Chrome Extension Developer</Role>

    <Meta>Google Chrome Extension</Meta>

    <Meta>Published Product</Meta>

    <Badge>LIVE PRODUCT</Badge>

    <SkillWrapper>
      <Skill>JavaScript</Skill>
      <Skill>Groq AI</Skill>
      <Skill>LLaMA 3.3</Skill>
      <Skill>Chrome Extension API</Skill>
      <Skill>AI Automation</Skill>
    </SkillWrapper>

  </CenterLeft>


  <Card
    variants={cardVariants}
    initial={{ y: 60, opacity: 0 }}
    animate={showContent ? { y: 0, opacity: 1 } : {}}
    transition={{ duration: 0.8 }}
  >

    <RightText>
      Built and published an AI-powered Chrome extension that
      automatically generates answers for Google Forms quizzes
      using Groq AI with the LLaMA 3.3 (70B) model.
    </RightText>

    <BulletList>

      <BulletItem>
        Developed a Chrome Extension that detects quiz questions from Google Forms
      </BulletItem>

      <BulletItem>
        Integrated Groq AI API to generate intelligent responses in real-time
      </BulletItem>

      <BulletItem>
        Implemented automated answer suggestions for MCQ-based quizzes
      </BulletItem>

      <BulletItem>
        Designed lightweight browser-based architecture ensuring user data stays local
      </BulletItem>

      <BulletItem>
        Published the extension with 30+ active users on Google Chrome
      </BulletItem>

    </BulletList>

  </Card>

</Row>


<Row>

  <CenterLeft
    variants={cardVariants}
    initial={{ y: 60, opacity: 0 }}
    animate={showContent ? { y: 0, opacity: 1 } : {}}
    transition={{ duration: 0.8 }}
  >

    <Company>DecentraShare</Company>

    <Role>Full Stack Developer</Role>

    <Meta>Secure Real-Time P2P File Transfer System</Meta>

    <Meta>Academic Project</Meta>

    <Badge>PROJECT</Badge>

    <SkillWrapper>
      <Skill>Rust</Skill>
      <Skill>TypeScript</Skill>
      <Skill>Next.js</Skill>
      <Skill>WebSockets</Skill>
      <Skill>Tailwind CSS</Skill>
    </SkillWrapper>

  </CenterLeft>



  <Card
    variants={cardVariants}
    initial={{ y: 60, opacity: 0 }}
    animate={showContent ? { y: 0, opacity: 1 } : {}}
    transition={{ duration: 0.8 }}
  >

    <RightText>
      Developed a secure real-time peer-to-peer file transfer system
      using a Finite State Machine (FSM) driven architecture to
      manage communication flow between peers.
    </RightText>

    <BulletList>

      <BulletItem>
        Built backend services using Rust and Axum framework
      </BulletItem>

      <BulletItem>
        Developed frontend interface using Next.js and TypeScript
      </BulletItem>

      <BulletItem>
        Implemented real-time communication using WebSockets
      </BulletItem>

      <BulletItem>
        Designed responsive UI using Tailwind CSS
      </BulletItem>

      <BulletItem>
        Simulated peer-to-peer environment for secure file transfer testing
      </BulletItem>

    </BulletList>

  </Card>

</Row>


<Row>

  <CenterLeft
    variants={cardVariants}
    initial={{ y: 60, opacity: 0 }}
    animate={showContent ? { y: 0, opacity: 1 } : {}}
    transition={{ duration: 0.8 }}
  >

    <Company>Student Management System</Company>

    <Role>Backend & Database Developer</Role>

    <Meta>DBMS Academic Project</Meta>

    <Meta>GitHub Repository</Meta>

    <Badge>PROJECT</Badge>

    <SkillWrapper>
      <Skill>Python</Skill>
      <Skill>SQL</Skill>
      <Skill>DBMS</Skill>
      <Skill>HTML</Skill>
      <Skill>CSS</Skill>
    </SkillWrapper>

  </CenterLeft>


  <Card
    variants={cardVariants}
    initial={{ y: 60, opacity: 0 }}
    animate={showContent ? { y: 0, opacity: 1 } : {}}
    transition={{ duration: 0.8 }}
  >

    <RightText>
      Developed a database-driven student management system to handle
      student records, academic data, and administrative operations
      using Python and SQL.
    </RightText>

    <BulletList>

      <BulletItem>
        Designed relational database schema for managing student information
      </BulletItem>

      <BulletItem>
        Implemented CRUD operations for student data management
      </BulletItem>

      <BulletItem>
        Built backend logic using Python for database interaction
      </BulletItem>

      <BulletItem>
        Developed frontend templates for displaying student records
      </BulletItem>

      <BulletItem>
        Integrated SQL database queries for efficient data retrieval
      </BulletItem>

    </BulletList>

  </Card>

</Row>


<Row>

  <CenterLeft
    variants={cardVariants}
    initial={{ y: 60, opacity: 0 }}
    animate={showContent ? { y: 0, opacity: 1 } : {}}
    transition={{ duration: 0.8 }}
  >

    <Company>Coffee Business Website</Company>

    <Role>Freelance Frontend Developer</Role>

    <Meta>Client Project</Meta>

    <Meta>Freelance Work</Meta>

    <Badge>FREELANCE</Badge>

    <SkillWrapper>
      <Skill>HTML</Skill>
      <Skill>CSS</Skill>
      <Skill>JavaScript</Skill>
      <Skill>Responsive Design</Skill>
      <Skill>UI Development</Skill>
    </SkillWrapper>

  </CenterLeft>



  <Card
    variants={cardVariants}
    initial={{ y: 60, opacity: 0 }}
    animate={showContent ? { y: 0, opacity: 1 } : {}}
    transition={{ duration: 0.8 }}
  >

    <RightText>
      Designed and developed a responsive coffee business website
      for a client to showcase products, services, and brand identity.
    </RightText>

    <BulletList>

      <BulletItem>
        Built responsive user interface using HTML, CSS, and JavaScript
      </BulletItem>

      <BulletItem>
        Designed modern layout for showcasing coffee products and menu
      </BulletItem>

      <BulletItem>
        Integrated images and media for visual product presentation
      </BulletItem>

      <BulletItem>
        Optimized website structure for better user experience
      </BulletItem>

      <BulletItem>
        Delivered the project as a freelance web development service
      </BulletItem>

    </BulletList>

  </Card>

</Row>


      </Wrapper>

    </MainContainer>
  )
}

export default Experience